import React, { useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  message,
  Spin,
  Select,
  Table,
  Popconfirm,
} from "antd";
import axios from "axios";
import {
  baseUrl,
  HEADERS_EXPORT,
  PUT_USER_ON_WORKLOG,
} from "../../../API/apiUrls";

// หา USER_ID ผู้รับผิดชอบจาก working_log ของ WORK_LOG_ID ปัจจุบัน
export const getResponsibleId = (record) => {
  const workLog =
    record?.working_log_list?.find((item) => item.id === record.WORK_LOG_ID) ||
    record?.working_log_list?.[0];
  return workLog?.USER_ID ?? null;
};

// ตัวเลือกผู้รับผิดชอบ (ตัด "ทั้งหมด" ออก) และเติม user ที่ไม่อยู่ใน options เช่น user ที่ปิดใช้งาน
export const buildResponsibleOptions = (
  lawyersOption,
  lawyersList,
  userIds,
) => {
  const options = (lawyersOption || []).filter((item) => item.value !== "all");
  userIds.forEach((id) => {
    if (id != null && !options.some((opt) => opt.value === id)) {
      const user = lawyersList?.find((item) => item.id === id);
      if (user) {
        options.push({ value: user.id, label: user.NNAME });
      }
    }
  });
  return options;
};

export const putResponsible = (workLogId, userId) =>
  axios.put(
    baseUrl + PUT_USER_ON_WORKLOG,
    { id: workLogId, USER_ID: userId },
    { headers: HEADERS_EXPORT },
  );

const EditResponsible = ({
  open,
  close,
  dataList,
  lawyersOption,
  lawyersList,
  funcReload,
}) => {
  const [loading, setLoading] = useState(false);
  const [searchContno, setSearchContno] = useState("");
  // เก็บเฉพาะ record ที่แก้ไข { [record.id]: USER_ID }
  const [editedUsers, setEditedUsers] = useState({});

  const options = useMemo(
    () =>
      buildResponsibleOptions(
        lawyersOption,
        lawyersList,
        (dataList || []).map(getResponsibleId),
      ),
    [lawyersOption, lawyersList, dataList],
  );

  const filteredList = useMemo(() => {
    const keyword = searchContno.trim();
    if (!keyword) return dataList || [];
    return (dataList || []).filter((item) => item?.CONTNO?.includes(keyword));
  }, [dataList, searchContno]);

  const changedList = (dataList || []).filter(
    (item) =>
      item.WORK_LOG_ID &&
      editedUsers[item.id] !== undefined &&
      editedUsers[item.id] !== getResponsibleId(item),
  );

  const handleCancel = () => {
    close(false);
  };

  const onChangeSelect = (value, record) => {
    setEditedUsers((prev) => ({ ...prev, [record.id]: value }));
  };

  const onConfirm = async () => {
    if (changedList.length === 0) {
      message.warning("ไม่มีข้อมูลที่เปลี่ยนแปลง");
      return;
    }

    setLoading(true);
    const failed = [];
    for (const item of changedList) {
      try {
        const res = await putResponsible(
          item.WORK_LOG_ID,
          editedUsers[item.id],
        );
        if (res.status !== 200) failed.push(item.CONTNO);
      } catch (error) {
        console.error(`แก้ไขผู้รับผิดชอบ ${item.CONTNO} ไม่สำเร็จ:`, error);
        failed.push(item.CONTNO);
      }
    }
    setLoading(false);

    if (failed.length > 0) {
      message.error(`แก้ไขไม่สำเร็จ: ${failed.join(", ")}`);
    } else {
      message.success(`แก้ไขผู้รับผิดชอบสำเร็จ ${changedList.length} สัญญา`);
    }
    funcReload();
    handleCancel();
  };

  const columns = [
    {
      title: "ลำดับ",
      align: "center",
      width: 70,
      render: (text, record, index) => index + 1,
    },
    {
      title: "เลขที่สัญญา",
      align: "center",
      dataIndex: "CONTNO",
    },
    {
      title: "ชื่อ-นามสกุล",
      align: "center",
      render: (record) => (
        <>
          {`${record?.CUSTOMER_TNAME || ""}${record?.CUSTOMER_FNAME || ""} ${
            record?.CUSTOMER_LNAME || ""
          }`}
        </>
      ),
    },
    {
      title: "ผู้รับผิดชอบ",
      align: "center",
      width: 260,
      render: (record) => (
        <Select
          showSearch
          placeholder="เลือกผู้รับผิดชอบ"
          optionFilterProp="label"
          value={
            (editedUsers[record.id] !== undefined
              ? editedUsers[record.id]
              : getResponsibleId(record)) ?? undefined
          }
          onChange={(value) => onChangeSelect(value, record)}
          options={options}
          disabled={!record.WORK_LOG_ID}
          style={{ width: "100%" }}
        />
      ),
    },
  ];

  return (
    <Modal
      title="แก้ไขผู้รับผิดชอบ"
      open={open}
      onCancel={handleCancel}
      width={900}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          ยกเลิก
        </Button>,
        <Popconfirm
          key="submit"
          title="แก้ไขผู้รับผิดชอบ"
          description={`คุณต้องการบันทึกการแก้ไขผู้รับผิดชอบ ${changedList.length} สัญญาใช่หรือไม่ ?`}
          onConfirm={onConfirm}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
          disabled={changedList.length === 0}
        >
          <Button
            type="primary"
            disabled={changedList.length === 0}
            loading={loading}
          >
            บันทึก
          </Button>
        </Popconfirm>,
      ]}
    >
      <Spin spinning={loading} size="large" tip=" Loading... ">
        <Input
          placeholder="ค้นหาเลขที่สัญญา"
          value={searchContno}
          onChange={(e) => setSearchContno(e.target.value)}
          allowClear
          style={{ width: 250, marginBottom: 10 }}
        />
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={filteredList}
          scroll={{ y: 500 }}
          footer={() => (
            <p style={{ margin: 0 }}>
              จำนวนสัญญาทั้งหมด {filteredList.length} | แก้ไขแล้ว{" "}
              {changedList.length}
            </p>
          )}
        />
      </Spin>
    </Modal>
  );
};

export default EditResponsible;
